import type { ExperimentConfig } from '@/types';

/**
 * Experiment 10: Volumetric Analysis of Antacid Tablets
 * CHEM-1131 Laboratory
 *
 * Students perform direct titration to determine the neutralizing capacity
 * of commercial antacid tablets in milliequivalents (mEq).
 */
const experiment10Config: ExperimentConfig = {
  id: 'exp10',
  number: 10,
  name: 'Antacid Tablet Analysis',
  description: 'Determine the acid-neutralizing capacity of commercial antacid tablets using direct titration with 0.2 M HCl solution and methyl orange indicator. Express results in milliequivalents (mEq).',
  estimatedTime: '90 minutes',
  learningObjectives: [
    'Understand the concept of direct titration of a base with acid',
    'Calculate milliequivalents (mEq) of neutralizing capacity',
    'Compare effectiveness of different antacid brands',
    'Use methyl orange indicator for acid-base titrations',
    'Apply stoichiometry to real-world products'
  ],
  safetyNotes: [
    'HCl is corrosive - handle with care',
    'Wear goggles during all titration procedures',
    'Clean up spills immediately',
    'Do not consume antacid tablets from the lab',
    'Never return unused chemicals to reagent bottles — dispose as directed by instructor'
  ],
  requiredEquipment: [
    '50 mL burette (Class A)',
    'Burette clamp and stand',
    'Mortar and pestle',
    'Watch glass (to cover mortar)',
    'Glass stirring rod',
    'Analytical balance',
    '250 mL Erlenmeyer flasks (2-3)',
    'Graduated cylinder (50 mL)',
    'White tile or paper (under flask for color observation)',
    'Weighing boat or paper',
    'Methyl orange indicator (0.1% in ethanol)',
    'Wash bottle'
  ],
  parts: [
    {
      id: 1,
      name: 'introduction',
      title: 'Part I: Introduction and Setup',
      equipment: ['Antacid tablets', 'Mortar and pestle', 'Balance'],
      initialState: {
        safetyAcknowledged: false,
        antacidType: null,
        tabletMass: null
      },
      steps: [
        {
          id: 1,
          title: 'Safety Briefing',
          shortDescription: 'Review safety procedures for acid-base titrations',
          handler: 'handleSafetyAcknowledgement',
          infoContent: {
            title: 'Understanding Antacids',
            content: 'Antacids contain basic compounds that neutralize stomach acid (HCl). Common active ingredients include: Mg(OH)₂ (Milk of Magnesia), CaCO₃ (Tums), NaHCO₃ (Eno, Brioschi), and NaAl(OH)₂CO₃ (Rolaids). We measure neutralizing capacity in milliequivalents (mEq). A common standard is that the antacid must contain at least **5 mEq/dose unit** of neutralizing capacity and raise the pH of gastric secretions to **3.5 or greater within 10 minutes**.',
            whatHappens: 'Safety protocols and the concept of antacid neutralizing capacity are reviewed before beginning the direct titration analysis.',
            observe: [
              'Note that different antacid brands contain different active ingredients',
              'The minimum standard is 5 mEq of neutralizing capacity per dose unit',
              'Look up your selected antacid brand to identify its active ingredient',
            ],
          }
        },
        {
          id: 2,
          title: 'Select Antacid',
          shortDescription: 'Choose an antacid tablet for analysis',
          handler: 'handleSelectAntacid',
          infoContent: {
            title: 'Antacid Comparison',
            content: 'Different brands contain different active ingredients and amounts. Comparing mEq/tablet tells us which is most effective at neutralizing acid. **Pre-lab requirement:** Look up your antacid brand to identify the active ingredient and stated amount per tablet before coming to lab.',
            whatHappens: 'An antacid tablet is selected for analysis; different brands contain different base compounds and will have different neutralizing capacities.',
            observe: [
              'Record the brand name and active ingredient of the selected antacid',
              'Note the stated amount of active ingredient per tablet from the packaging',
              'This information will be compared to your experimental results',
            ],
          }
        },
        {
          id: 3,
          title: 'Weigh Tablet',
          shortDescription: 'Record the mass of the antacid tablet',
          handler: 'handleWeighTablet'
        },
        {
          id: 4,
          title: 'Crush Tablet',
          shortDescription: 'Grind the tablet to a fine powder in the mortar',
          handler: 'handleCrushTablet',
          infoContent: {
            title: 'Why Crush?',
            content: 'Crushing increases surface area, allowing faster and more complete reaction with the acid. This ensures accurate results.',
            whatHappens: 'The tablet is ground to a fine powder using a mortar and pestle to dramatically increase surface area for complete and rapid reaction with acid.',
            observe: [
              'Cover the mortar with a watch glass while crushing to prevent loss',
              'Grind until the tablet is a uniform fine powder',
              'The increased surface area ensures complete reaction during titration',
            ],
          }
        }
      ]
    },
    {
      id: 2,
      name: 'sample_preparation',
      title: 'Part II: Sample Preparation',
      equipment: ['250 mL Erlenmeyer flask', 'Graduated cylinder', 'Deionized water'],
      initialState: {
        sampleTransferred: false,
        waterAdded: false,
        slurryColor: '#F5F5F5',
        indicatorAdded: false,
        solutionColor: '#FFA500'
      },
      steps: [
        {
          id: 1,
          title: 'Transfer to Flask',
          shortDescription: 'Transfer crushed tablet to a 250 mL Erlenmeyer flask',
          handler: 'handleTransferToFlask'
        },
        {
          id: 2,
          title: 'Add Water',
          shortDescription: 'Add 50 mL of deionized water to create a slurry',
          handler: 'handleAddWater',
          infoContent: {
            title: 'Slurry Formation',
            content: 'The antacid may not fully dissolve - this is normal. Stir with a glass stirring rod to get a solution or slurry. Consider: Is the substance we are titrating very soluble? Keep your solutions once they are titrated.',
            whatHappens: 'Deionized water is added to the crushed tablet to create a slurry or suspension; many antacid compounds are only partially soluble in water.',
            observe: [
              'The mixture forms a milky white slurry rather than a clear solution',
              'Stir thoroughly with a glass stirring rod to disperse the powder',
              'The incomplete dissolution is normal for many antacid compounds',
            ],
          }
        },
        {
          id: 3,
          title: 'Add Indicator',
          shortDescription: 'Add 10 drops of methyl orange indicator, then swirl gently to mix',
          handler: 'handleAddIndicator',
          infoContent: {
            title: 'Methyl Orange Indicator',
            content: 'Add **10 drops** of methyl orange indicator (0.1% in ethanol), then **swirl gently to mix**. The solution should appear yellow-orange (basic). Methyl orange is yellow in base (pH > 4.4) and red in acid (pH < 3.1). We start with yellow (basic antacid) and titrate until pink-red (excess acid).',
            whatHappens: 'Methyl orange indicator is added to the basic antacid slurry, turning it yellow-orange; it will shift to pink-red when excess acid is present at the endpoint.',
            observe: [
              'The slurry turns yellow-orange after adding 10 drops of methyl orange',
              'The yellow color confirms the solution is basic (pH > 4.4)',
              'Swirl gently to distribute the indicator evenly throughout the slurry',
            ],
          }
        }
      ]
    },
    {
      id: 3,
      name: 'burette_setup',
      title: 'Part III: Burette Preparation',
      equipment: ['50 mL burette', 'Burette clamp', '0.2 M HCl solution', 'Funnel'],
      initialState: {
        buretteFilled: false,
        buretteReading: 0,
        stopcockPrimed: false
      },
      steps: [
        {
          id: 1,
          title: 'Rinse Burette',
          shortDescription: 'Rinse burette with small portion of 0.2 M HCl',
          handler: 'handleRinseBurette'
        },
        {
          id: 2,
          title: 'Fill Burette',
          shortDescription: 'Fill burette with 0.2 M HCl solution',
          handler: 'handleFillBurette',
          infoContent: {
            title: 'HCl Concentration',
            content: 'We use 0.2 M HCl for this titration. The concentration affects mEq calculations: mEq = M × V(mL) for monoprotic acids.',
            whatHappens: 'The burette is filled with 0.2 M HCl solution after being conditioned with a small rinse of the same acid to prevent dilution.',
            observe: [
              'Fill the burette completely with 0.2 M HCl solution',
              'The HCl solution is colorless',
              'Ensure no air bubbles are trapped below the stopcock',
            ],
          }
        },
        {
          id: 3,
          title: 'Prime Stopcock',
          shortDescription: 'Remove air bubbles from burette tip',
          handler: 'handlePrimeStopcock'
        },
        {
          id: 4,
          title: 'Record Initial Reading',
          shortDescription: 'Read and record the initial burette reading',
          handler: 'handleRecordInitial',
          infoContent: {
            title: 'Burette Precision',
            content: 'Read the meniscus at eye level. Record to 0.01 mL. The initial reading may not be exactly 0.00 mL.',
            whatHappens: 'The initial burette reading is recorded to 0.01 mL precision by reading the bottom of the meniscus at eye level.',
            observe: [
              'Position eyes level with the meniscus for accurate reading',
              'Record to 0.01 mL precision',
              'The initial reading may not start at exactly 0.00 mL',
            ],
          }
        }
      ]
    },
    {
      id: 4,
      name: 'titration',
      title: 'Part IV: Perform Titration',
      equipment: ['Burette with HCl', 'Flask with sample', 'White tile or paper'],
      initialState: {
        isStirring: false,
        solutionColor: '#FFA500',
        acidAdded: 0,
        endpointReached: false,
        finalReading: null
      },
      steps: [
        {
          id: 1,
          title: 'Prepare for Titration',
          shortDescription: 'Record initial burette reading and prepare to swirl flask gently during titration',
          handler: 'handleStartStirrer'
        },
        {
          id: 2,
          title: 'Begin Titration',
          shortDescription: 'Place flask on white tile and add HCl slowly while watching for color change',
          handler: 'handleBeginTitration',
          infoContent: {
            title: 'Titration Technique',
            content: 'Place the flask on a **white tile or paper** for better color observation. Add acid slowly from the burette while **swirling gently** after each addition. Titrate until a **pink colour persists** — this is the endpoint.',
            whatHappens: 'HCl is added slowly from the burette to the yellow-orange antacid slurry while swirling; the color gradually shifts toward orange as the base is consumed.',
            observe: [
              'Place the flask on a white tile for better color detection',
              'Swirl gently after each addition of HCl to ensure thorough mixing',
              'The solution color shifts from yellow to orange as acid is added',
            ],
          }
        },
        {
          id: 3,
          title: 'Approach Endpoint',
          shortDescription: 'Add HCl drop by drop as color changes to orange',
          handler: 'handleApproachEndpoint'
        },
        {
          id: 4,
          title: 'Detect Endpoint',
          shortDescription: 'Stop when solution turns persistent pink/red',
          handler: 'handleDetectEndpoint',
          validation: {
            requires: ['endpointReached']
          },
          infoContent: {
            title: 'Endpoint Color',
            content: 'The endpoint is when the solution changes from yellow-orange to pink-red and the color persists. At this point, excess HCl is present.',
            whatHappens: 'The endpoint is reached when the methyl orange indicator turns from yellow-orange to a persistent pink-red, indicating all the base has been neutralized with slight excess acid.',
            observe: [
              'The solution changes from orange to a persistent pink-red color',
              'The pink color should remain after 30 seconds of swirling',
              'This indicates all the antacid base has been neutralized by HCl',
            ],
          }
        },
        {
          id: 5,
          title: 'Record Final Reading',
          shortDescription: 'Record the final burette reading',
          handler: 'handleRecordFinal'
        }
      ]
    },
    {
      id: 5,
      name: 'repeat_trials',
      title: 'Part V: Repeat Trials',
      equipment: ['Fresh antacid tablets', 'Clean flasks'],
      initialState: {
        trials: [],
        currentTrial: 1
      },
      steps: [
        {
          id: 1,
          title: 'Prepare New Sample',
          shortDescription: 'Weigh and crush a new tablet for trial 2',
          handler: 'handlePrepareNewSample'
        },
        {
          id: 2,
          title: 'Repeat Titration',
          shortDescription: 'Perform titration on the new sample',
          handler: 'handleRepeatTitration'
        },
        {
          id: 3,
          title: 'Check Concordance',
          shortDescription: 'Compare results from both trials',
          handler: 'handleCheckConcordance',
          infoContent: {
            title: 'Concordant Results',
            content: 'Compare the results from both trials. Results should agree for good precision. Use the average for your mEq calculations.',
            whatHappens: 'The titre volumes from multiple trials are compared; concordant results confirm the reliability of the data for mEq calculations.',
            observe: [
              'Compare the HCl volumes used in each trial',
              'Results should agree within acceptable precision for concordance',
              'Use the average of concordant values for the final calculation',
            ],
          }
        }
      ]
    },
    {
      id: 6,
      name: 'calculations',
      title: 'Part VI: Calculations',
      equipment: ['Calculator', 'Data table'],
      initialState: {
        averageVolume: null,
        mEqHCl: null,
        mEqNeutralized: null,
        mEqPerTablet: null,
        mEqPerGram: null
      },
      steps: [
        {
          id: 1,
          title: 'Calculate Average Volume',
          shortDescription: 'Average the concordant HCl volumes',
          handler: 'handleCalculateAverage'
        },
        {
          id: 2,
          title: 'Calculate mEq HCl',
          shortDescription: 'Calculate milliequivalents of HCl used',
          handler: 'handleCalculateMEqHCl',
          infoContent: {
            title: 'mEq Calculation',
            content: 'mEq stands for milliequivalent (1/1000th of an equivalent). For monoprotic HCl: 1 mEq HCl = 1 millimole HCl (because HCl has only one hydrogen ion to lose). The millimoles of HCl consumed = molarity (millimoles/mL) x mL used. With 0.2 M HCl: mEq = 0.2 × Volume(mL).',
            whatHappens: 'Milliequivalents of HCl consumed are calculated from the average titre volume and the HCl concentration using the formula mEq = M x V(mL).',
            observe: [
              'For monoprotic HCl, 1 mEq = 1 millimole',
              'Calculate: mEq = 0.2 M x average volume in mL',
              'This represents the total acid neutralized by the antacid tablet',
            ],
          }
        },
        {
          id: 3,
          title: 'Calculate mEq Neutralized',
          shortDescription: 'Determine acid-neutralizing capacity (mEq/dose unit)',
          handler: 'handleCalculateNeutralized',
          infoContent: {
            title: 'Neutralizing Capacity',
            content: 'The mEq neutralized by the antacid equals the millimoles of HCl consumed (since 1 mEq HCl = 1 millimole HCl). The millimoles of HCl consumed = molarity (millimoles/mL) x mL used. Standard: Antacids must contain at least **5 mEq/dose unit** of neutralizing capacity.',
            whatHappens: 'The acid-neutralizing capacity is expressed in mEq per dose unit and compared to the 5 mEq/dose minimum standard for antacid products.',
            observe: [
              'The mEq neutralized equals the millimoles of HCl consumed',
              'Compare your result to the minimum standard of 5 mEq per dose unit',
              'Higher mEq values indicate a more effective antacid',
            ],
          }
        },
        {
          id: 4,
          title: 'Calculate mEq per Tablet',
          shortDescription: 'Express neutralizing capacity per tablet',
          handler: 'handleCalculatePerTablet'
        },
        {
          id: 5,
          title: 'Compare Results',
          shortDescription: 'Compare to label claims and other brands',
          handler: 'handleCompareResults',
          infoContent: {
            title: 'Quality Assessment',
            content: 'Compare your mEq value to the manufacturer\'s claim. Differences may indicate incomplete reaction, expired product, or measurement error.',
            whatHappens: 'The experimental mEq value is compared to the manufacturer\'s label claim and to results from other antacid brands tested by classmates.',
            observe: [
              'Compare your mEq result with the manufacturer\'s stated value',
              'Discuss possible reasons for any discrepancies',
              'Compare effectiveness across different antacid brands in the class',
            ],
          }
        }
      ]
    }
  ],
  quiz: {
    experimentId: 'exp10',
    title: 'Antacid Analysis Quiz',
    questions: [
      {
        id: 'exp10-q1',
        type: 'mcq' as const,
        question: 'What indicator is used in the titration of antacid tablets with HCl?',
        options: [
          { id: 'a', text: 'Phenolphthalein' },
          { id: 'b', text: 'Methyl orange' },
          { id: 'c', text: 'Bromothymol blue' },
          { id: 'd', text: 'Universal indicator' },
        ],
        correctOptionId: 'b',
        explanation: 'Methyl orange is used because the endpoint of this titration occurs in the acidic range (around pH 3.1-4.4). Methyl orange changes from yellow/orange (basic) to red/pink (acidic) in this pH range, making it suitable for strong acid-weak base titrations.',
      },
      {
        id: 'exp10-q2',
        type: 'mcq' as const,
        question: 'What does "milliequivalents (mEq)" measure in the context of antacid analysis?',
        options: [
          { id: 'a', text: 'The mass of the antacid tablet' },
          { id: 'b', text: 'The number of millimoles of acid that can be neutralized' },
          { id: 'c', text: 'The pH of the antacid solution' },
          { id: 'd', text: 'The volume of HCl needed' },
        ],
        correctOptionId: 'b',
        explanation: 'Milliequivalents (mEq) represent the neutralizing capacity of the antacid - specifically, how many millimoles of H⁺ ions the antacid can neutralize. One mEq equals one millimole of monovalent acid (like HCl). A minimum of 5 mEq per dose unit is a common standard.',
      },
      {
        id: 'exp10-q3',
        type: 'mcq' as const,
        question: 'In the direct titration method, the antacid tablet is:',
        options: [
          { id: 'a', text: 'Dissolved in excess NaOH, then titrated with HCl' },
          { id: 'b', text: 'Dissolved directly and titrated with standardized HCl' },
          { id: 'c', text: 'Heated until decomposed, then titrated' },
          { id: 'd', text: 'Dissolved in water and the pH is measured directly' },
        ],
        correctOptionId: 'b',
        explanation: 'In the direct titration method, the crushed antacid tablet is placed in a flask with water, and standardized HCl (0.2 M) is added from a burette until the indicator changes color, showing that all the base in the antacid has been neutralized.',
      },
      {
        id: 'exp10-q4',
        type: 'short_answer' as const,
        question: 'Explain why the antacid tablet must be crushed into a fine powder before titration. What error might result from using whole or large pieces?',
        sampleAnswer: 'Crushing the tablet into fine powder increases the surface area exposed to the acid, ensuring the reaction goes to completion during the titration. If the tablet is in large pieces, the interior may not react completely with the HCl, leading to an underestimate of the neutralizing capacity. Unreacted antacid inside larger particles would give a falsely low mEq value, making the antacid appear less effective than it actually is.',
      },
      {
        id: 'exp10-q5',
        type: 'calculation' as const,
        question: 'If 35.20 mL of 0.200 M HCl is required to titrate a 1.250 g antacid tablet to the methyl orange endpoint, what is the neutralizing capacity in mEq?',
        expectedValue: 7.04,
        tolerance: 2,
        unit: 'mEq',
      },
    ],
  },
};

// Methyl orange indicator color data
export const METHYL_ORANGE_COLORS = {
  basic: {
    color: '#FFD700',
    description: 'Yellow (basic conditions)',
    pHRange: '> 4.4'
  },
  transition1: {
    color: '#FFA500',
    description: 'Yellow-Orange',
    pHRange: '4.4 - 4.0'
  },
  transition2: {
    color: '#FF8C00',
    description: 'Orange',
    pHRange: '4.0 - 3.5'
  },
  transition3: {
    color: '#FF6347',
    description: 'Red-Orange (near endpoint)',
    pHRange: '3.5 - 3.1'
  },
  acidic: {
    color: '#FF4500',
    description: 'Red (acidic - endpoint)',
    pHRange: '< 3.1'
  }
};

// Solution colors during titration
export const SOLUTION_COLORS = {
  slurryInitial: { color: '#F5F5F5', description: 'Milky white slurry' },
  withIndicatorBasic: { color: '#FFA500', description: 'Yellow-orange (basic)' },
  nearEndpoint: { color: '#FF8C00', description: 'Orange (transition)' },
  atEndpoint: { color: '#FF6347', description: 'Red-orange (endpoint)' },
  pastEndpoint: { color: '#FF4500', description: 'Red (excess acid)' }
};

// Equipment colors
export const EQUIPMENT_COLORS = {
  buretteGlass: { color: '#E8F4F8', description: 'Clear with slight blue' },
  buretteGraduations: { color: '#1E3A5F', description: 'Dark blue markings' },
  erlenmeyerFlask: { color: '#F5F5F5', description: 'Clear glass' },
  mortarCeramic: { color: '#FFFEF0', description: 'White/cream ceramic' },
  pestleCeramic: { color: '#FFFEF0', description: 'White/cream ceramic' },
  stopcock: { color: '#FFFFFF', description: 'White PTFE' }
};

// Antacid data
export const ANTACID_TYPES = {
  tums: {
    name: 'Tums',
    activeIngredient: 'CaCO₃',
    typicalMEq: 10,
    color: '#FFFFFF'
  },
  rolaids: {
    name: 'Rolaids',
    activeIngredient: 'NaAl(OH)₂CO₃',
    typicalMEq: 7.5,
    color: '#F5F5F5'
  },
  milkOfMagnesia: {
    name: 'Milk of Magnesia',
    activeIngredient: 'Mg(OH)₂',
    typicalMEq: 12,
    color: '#FFFFFF'
  },
  eno: {
    name: 'Eno',
    activeIngredient: 'NaHCO₃',
    typicalMEq: 8,
    color: '#FFFFFF'
  },
  brioschi: {
    name: 'Brioschi',
    activeIngredient: 'NaHCO₃',
    typicalMEq: 8,
    color: '#FFFFFF'
  },
  generic: {
    name: 'Generic Antacid',
    activeIngredient: 'Various',
    typicalMEq: 8,
    color: '#F0F0F0'
  }
};

// Standard requirement
export const MEQ_STANDARD = {
  minimumPerDose: 5,
  description: 'A common standard requires antacids to contain at least 5 mEq/dose unit of neutralizing capacity'
};

export default experiment10Config;
